import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import './DashboardAgenda.css';
import baseURL from '../../url';

const STICKY_CLASSES = ['sticky-yellow', 'sticky-green', 'sticky-blue', 'sticky-rose'];

function formatCreatedAt(value) {
    if (!value) {
        return '';
    }

    const parsed = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

function getStickyClass(item, index) {
    const seed = Number(item?.idDashboardAgendaEntry || index || 0);
    return STICKY_CLASSES[seed % STICKY_CLASSES.length];
}

export default function DashboardAgenda() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [activeNote, setActiveNote] = useState(null);

    const loadAgenda = () => {
        setLoading(true);
        fetch(`${baseURL}/dashboardAgendaGet.php`, {
            method: 'GET',
            cache: 'no-store',
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.error) {
                    throw new Error(data.error);
                }

                const sortedItems = [...(data.items || [])].sort((left, right) => {
                    if (Number(left.completed) !== Number(right.completed)) {
                        return Number(left.completed) - Number(right.completed);
                    }

                    return new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime();
                });

                setItems(sortedItems);
            })
            .catch((error) => {
                console.error('Error al cargar notas:', error);
                toast.error(error.message || 'No se pudieron cargar las notas.');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAgenda();
    }, []);

    const submitNote = async (event) => {
        event.preventDefault();

        const trimmedNote = noteText.trim();
        if (!trimmedNote) {
            toast.error('Escribe una nota primero.');
            return;
        }

        const firstLine = trimmedNote.split('\n').find((line) => line.trim()) || trimmedNote;
        const title = firstLine.trim().slice(0, 80);

        try {
            setSaving(true);
            const response = await fetch(`${baseURL}/dashboardAgendaPost.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    title,
                    details: trimmedNote,
                }),
            });
            const data = await response.json();
            if (data.error) {
                throw new Error(data.error);
            }

            setNoteText('');
            loadAgenda();
        } catch (error) {
            console.error('Error al guardar nota:', error);
            toast.error(error.message || 'No se pudo guardar la nota.');
        } finally {
            setSaving(false);
        }
    };

    const toggleCompleted = async (idDashboardAgendaEntry, completed) => {
        try {
            const response = await fetch(`${baseURL}/dashboardAgendaPut.php?idDashboardAgendaEntry=${idDashboardAgendaEntry}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ completed }),
            });
            const data = await response.json();
            if (data.error) {
                throw new Error(data.error);
            }

            loadAgenda();
        } catch (error) {
            console.error('Error al actualizar nota:', error);
            toast.error(error.message || 'No se pudo actualizar la nota.');
        }
    };

    const deleteNote = async (idDashboardAgendaEntry) => {
        try {
            const response = await fetch(`${baseURL}/dashboardAgendaDelete.php?idDashboardAgendaEntry=${idDashboardAgendaEntry}`, {
                method: 'DELETE',
            });
            const data = await response.json();
            if (data.error) {
                throw new Error(data.error);
            }

            loadAgenda();
        } catch (error) {
            console.error('Error al eliminar nota:', error);
            toast.error(error.message || 'No se pudo eliminar la nota.');
        }
    };

    return (
        <section className='dashboardAgendaPanel'>
            <div className='dashboardAgendaHeader'>
                <h3>Notas</h3>
                <span className='dashboardAgendaCounter'>{items.filter((item) => Number(item.completed) !== 1).length} abiertas</span>
            </div>

            <form className='dashboardAgendaComposer' onSubmit={submitNote}>
                <textarea
                    value={noteText}
                    onChange={(event) => setNoteText(event.target.value)}
                    placeholder='Escribe una nota...'
                    rows='2'
                />
                <button type='submit' disabled={saving}>
                    <FontAwesomeIcon icon={faPlus} />
                    {saving ? 'Guardando...' : 'Agregar'}
                </button>
            </form>

            <div className='dashboardAgendaList'>
                {loading ? (
                    <div className='dashboardAgendaEmpty'>Cargando notas...</div>
                ) : items.length === 0 ? (
                    <div className='dashboardAgendaEmpty'>Agrega una nota rapida y quedara guardada aqui.</div>
                ) : (
                    items.map((item, index) => (
                        <article
                            key={item.idDashboardAgendaEntry}
                            className={`dashboardStickyNote ${getStickyClass(item, index)} ${Number(item.completed) === 1 ? 'is-complete' : ''}`}
                            onClick={() => setActiveNote(item)}
                        >
                            <div className='dashboardStickyTop'>
                                <small>{formatCreatedAt(item.createdAt)}</small>
                                <div className='dashboardStickyActions'>
                                    <button
                                        type='button'
                                        className='dashboardStickyDone'
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggleCompleted(item.idDashboardAgendaEntry, Number(item.completed) !== 1);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faCheck} />
                                    </button>
                                    <button
                                        type='button'
                                        className='dashboardStickyDelete'
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            deleteNote(item.idDashboardAgendaEntry);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            </div>
                            <p>{item.details || item.title}</p>
                        </article>
                    ))
                )}
            </div>

            {activeNote ? (
                <div className='dashboardStickyModal' onClick={() => setActiveNote(null)}>
                    <div className={`dashboardStickyModalCard ${getStickyClass(activeNote, activeNote.idDashboardAgendaEntry)}`} onClick={(event) => event.stopPropagation()}>
                        <div className='dashboardStickyTop'>
                            <small>{formatCreatedAt(activeNote.createdAt)}</small>
                            <button type='button' className='dashboardStickyClose' onClick={() => setActiveNote(null)}>Cerrar</button>
                        </div>
                        <p>{activeNote.details || activeNote.title}</p>
                    </div>
                </div>
            ) : null}
        </section>
    );
}
